import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./contexts/AuthContext";
import { LoadingProvider } from "./contexts/LoadingContext";
import DashboardLayout from "./components/layout/DashboardLayout";
import Dashboard from "./pages/Dashboard";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import PrivateRoute from "./components/PrivateRoute";
import Documents from "./pages/Documents";
import AllDocuments from "./pages/AllDocuments";
import NotFound from "./pages/NotFound";
import MyTemplates from "./pages/MyTemplates";
import CreateTemplate from "./pages/CreateTemplate";
import CreateAITemplate from "./pages/CreateAITemplate";
import AIConversationDetail from "./pages/AIConversationDetail";
import ConfigureDocument from "./pages/ConfigureDocument";
import { pdfjs } from "react-pdf";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import UseTemplate from "./pages/UseTemplate";
import SignDocument from "./pages/SignDocument";
import GlobalLoader from "./components/GlobalLoader";
import Profile from "./pages/Profile";
import Organization from "./pages/Organization";
import OrganizationCompanies from "./pages/OrganizationCompanies";
import SubscriptionAndUsage from "./pages/SubscriptionAndUsage";
import Members from "./pages/Members";
import BulkSend from "./pages/BulkSend";
import OnDemandSigning from "./pages/OnDemandSigning";
import OnDemandSigningContainerDetailPage from "./pages/OnDemandSigningContainerDetailPage";
import Roles from "./pages/Roles";
import Signature from "./pages/Signature";
import SelfSign from "./pages/SelfSign";
import SelfSignDocumentPage from "./pages/SelfSignDocumentPage";
import PaymentSuccess from "./pages/PaymentSuccess";
import InvitePage from "./pages/InvitePage";
import OnDemandSignPortal from "./pages/OnDemandSignPortal";
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.mjs`;

const queryClient = new QueryClient();

const App = () => {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <DndProvider backend={HTML5Backend}>
          <Router>
            <AuthProvider>
              <LoadingProvider>
                <GlobalLoader />
                <Routes>
                  {/* Public routes */}
                  <Route
                    path="/"
                    element={<Navigate to="/dashboard" replace />}
                  />
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/verify-email" element={<VerifyEmail />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />

                  {/* Public invite route */}
                  <Route path="/invite/:token" element={<InvitePage />} />
                  <Route
                    path="/ondemand-sign/:token"
                    element={<OnDemandSignPortal />}
                  />

                  {/* Protected dashboard routes */}
                  <Route path="/dashboard" element={<PrivateRoute />}>
                    <Route element={<DashboardLayout />}>
                      <Route index element={<Dashboard />} />
                      <Route path="documents" element={<Documents />}>
                        <Route index element={<AllDocuments />} />
                        <Route path="signed" element={<AllDocuments />} />
                        <Route path="pending" element={<AllDocuments />} />
                        <Route path="expired" element={<AllDocuments />} />
                        <Route path="declined" element={<AllDocuments />} />
                        <Route path="archived" element={<AllDocuments />} />
                      </Route>
                      <Route path="templates" element={<MyTemplates />} />
                      <Route
                        path="templates/create"
                        element={<CreateTemplate />}
                      />
                      <Route
                        path="templates/create-ai"
                        element={<CreateAITemplate />}
                      />
                      <Route
                        path="templates/ai-conversations/:conversationId"
                        element={<AIConversationDetail />}
                      />
                      <Route
                        path="templates/update/:templateId"
                        element={<CreateTemplate />}
                      />
                      <Route
                        path="templates/configure-document/:documentId/:templateId"
                        element={<ConfigureDocument />}
                      />
                      <Route
                        path="templates/use-template/:templateId"
                        element={<UseTemplate />}
                      />
                      <Route path="members" element={<Members />} />
                      <Route path="bulk-send" element={<BulkSend />} />
                      <Route
                        path="ondemand-signing/containers/:containerId"
                        element={<OnDemandSigningContainerDetailPage />}
                      />
                      <Route
                        path="ondemand-signing"
                        element={<OnDemandSigning />}
                      />
                      <Route path="roles" element={<Roles />} />
                      <Route
                        path="subscription"
                        element={
                          <Navigate
                            to="/dashboard/organization/subscription"
                            replace
                          />
                        }
                      />
                      <Route path="organization" element={<Organization />}>
                        <Route index element={<OrganizationCompanies />} />
                        <Route
                          path="subscription"
                          element={<SubscriptionAndUsage />}
                        />
                      </Route>
                      <Route path="signature" element={<Signature />} />
                      <Route path="self-sign" element={<SelfSign />} />
                      <Route
                        path="self-sign/document"
                        element={<SelfSignDocumentPage />}
                      />
                      <Route path="profile" element={<Profile />} />
                      <Route
                        path="payment/success"
                        element={<PaymentSuccess />}
                      />
                    </Route>
                  </Route>

                  {/* Public signing route */}
                  <Route
                    path="/sign/:signReqId/:userId"
                    element={<SignDocument />}
                  />

                  {/* Catch all route */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
                <Toaster
                  position="top-center"
                  toastOptions={{
                    success: {
                      className:
                        "bg-white text-green-800 !px-8 !py-4 rounded-lg shadow-md",
                    },
                    error: {
                      className:
                        "bg-white text-red-800 !px-8 !py-4 rounded-lg shadow-md",
                    },
                  }}
                />
              </LoadingProvider>
            </AuthProvider>
          </Router>
        </DndProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
